// Status: active. Exact segment preview contracts (Segment Builder Redesign, SPEC §5).
// Counts are exact over the Pocketflows-eligible universe; "can get email/texts"
// follow the contact sync's channel reachability. No partial number is ever
// reported as exact: anything incomplete is `status: 'unavailable'`.
import { z } from 'zod';
import { audienceDigestSchema, audienceIdSchema, nativeAudienceDefinitionSchema } from './native-audience.js';

export const PREVIEW_SAMPLE_LIMIT = 4;
export const PREVIEW_PAGE_SIZE = 50;

const count = z.number().int().nonnegative().safe();
const isoTimestamp = z.string().datetime({ offset: true });

/**
 * Why Pocketflows refused a contact (ADM-227): the email or phone it rejected when the sync
 * tried to create the customer. Such a contact never becomes a Pocketflows customer.
 */
export const previewRejectedFieldSchema = z.enum(['email', 'phone']);
export type PreviewRejectedField = z.infer<typeof previewRejectedFieldSchema>;

/** A person shown in the preview. Phones are masked to their last 4 digits; nothing else leaves the server. */
export const previewPersonRowSchema = z.object({
  contactId: audienceIdSchema,
  name: z.string().max(512),
  primaryEmail: z.string().max(320).nullable(),
  primaryPhoneMasked: z.string().regex(/^•+ ?\d{1,4}$/).nullable(),
  why: z.enum(['rules', 'hand-picked']),
  canEmail: z.boolean(),
  canText: z.boolean(),
  /** Only on the `rejected` tab's rows: what Pocketflows refused (ADM-227). */
  rejected: z.array(previewRejectedFieldSchema).min(1).max(2).optional(),
}).strict();
export type PreviewPersonRow = z.infer<typeof previewPersonRowSchema>;

/**
 * A hand-picked person referenced by a builder warning. `name` is null when the
 * caller can't view contacts: counts are open to segment builders, names are not.
 */
export const previewPersonRefSchema = z.object({ contactId: audienceIdSchema, name: z.string().max(512).nullable() }).strict();
export type PreviewPersonRef = z.infer<typeof previewPersonRefSchema>;

export const segmentPreviewExactInputSchema = z.object({
  targetToken: audienceDigestSchema,
  definition: nativeAudienceDefinitionSchema,
  /**
   * Opt in to the `rejected` count (ADM-227). Opt-in for the same reason as the catalog's
   * `includeFieldOptions`: a strict client from before the key would refuse the whole result.
   */
  includeRejected: z.boolean().optional(),
}).strict();
export type SegmentPreviewExactInput = z.infer<typeof segmentPreviewExactInputSchema>;

export const segmentPreviewUnavailableReasonSchema = z.enum(['giving-rule', 'legacy', 'over-budget', 'index-unavailable', 'normalization']);
export type SegmentPreviewUnavailableReason = z.infer<typeof segmentPreviewUnavailableReasonSchema>;

export const segmentPreviewExactOutputSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('exact'),
    match: count,
    email: count,
    sms: count,
    /** Null when the definition isn't in the builder's canonical include/exclude shape. */
    breakdown: z.object({ rules: count, alwaysIncluded: count, excluded: count }).strict().nullable(),
    sample: z.array(previewPersonRowSchema).max(PREVIEW_SAMPLE_LIMIT),
    /** Hand-picked people who also match an exclusion. They are still included (Pocketflows static customers). */
    handPickedClashes: z.array(previewPersonRefSchema).max(1000),
    /** Hand-picked people Save would reject because they aren't synced to this marketing account. */
    handPickedUnsynced: z.array(previewPersonRefSchema).max(1000),
    /**
     * Contacts left out because their fund and form totals aren't synced (never computed, or
     * too much history). Sent only for a definition with a fund or form rule; absent means 0.
     */
    notCounted: count.optional(),
    /**
     * Contacts the rules match that Pocketflows refused (invalid email or phone, never created as a
     * customer). They are left out of every number above, as Pocketflows leaves them out of the
     * segment (ADM-227). Sent only to a caller that set `includeRejected`, and only when above 0;
     * absent means 0.
     */
    rejected: count.optional(),
    countedAt: isoTimestamp,
  }).strict(),
  z.object({ status: z.literal('unavailable'), reason: segmentPreviewUnavailableReasonSchema, countedAt: isoTimestamp }).strict(),
]).superRefine((v, ctx) => {
  if (v.status !== 'exact') return;
  if (v.email > v.match || v.sms > v.match) ctx.addIssue({ code: 'custom', message: 'Reachable counts cannot exceed the match count' });
  if (v.breakdown && v.breakdown.rules + v.breakdown.alwaysIncluded !== v.match) ctx.addIssue({ code: 'custom', path: ['breakdown'], message: 'Breakdown must add up to the match count' });
});
export type SegmentPreviewExactOutput = z.infer<typeof segmentPreviewExactOutputSchema>;

/** `rejected` (ADM-227): the matched contacts Pocketflows refused, the ones `rejected` counts. */
export const segmentPreviewTabSchema = z.enum(['all', 'email', 'sms', 'excluded', 'rejected']);
export type SegmentPreviewTab = z.infer<typeof segmentPreviewTabSchema>;

export const segmentPreviewPeopleInputSchema = z.object({
  targetToken: audienceDigestSchema,
  definition: nativeAudienceDefinitionSchema,
  tab: segmentPreviewTabSchema.default('all'),
  query: z.string().trim().max(120).optional(),
  page: z.number().int().min(1).max(100_000).default(1),
}).strict();
export type SegmentPreviewPeopleInput = z.input<typeof segmentPreviewPeopleInputSchema>;

export const segmentPreviewPeopleOutputSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('exact'),
    rows: z.array(previewPersonRowSchema).max(PREVIEW_PAGE_SIZE),
    page: z.number().int().min(1),
    pageCount: z.number().int().min(0),
    total: count,
    snapshotId: z.string().min(1).max(128),
  }).strict(),
  z.object({ status: z.literal('unavailable'), reason: segmentPreviewUnavailableReasonSchema }).strict(),
]);
export type SegmentPreviewPeopleOutput = z.infer<typeof segmentPreviewPeopleOutputSchema>;
